import React, { useState, useEffect, useMemo, useRef } from 'react';
import { User, Building2, Search, Linkedin } from 'lucide-react';

/* ─── Sub-components ────────────────────────────────────────────────── */

function SelectionCard({ title, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-[12px] p-4 text-left transition-all ${
        selected
          ? 'bg-[hsl(var(--succeedo-orange))]/5 border-2 border-[hsl(var(--succeedo-orange))]'
          : 'bg-[hsl(var(--background))] border border-[hsl(var(--border))] hover:border-[hsl(var(--muted-foreground))]'
      }`}
    >
      <div className="text-[14px] font-medium text-[hsl(var(--foreground))]">{title}</div>
    </button>
  );
}

function IndustryPill({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] transition-all ${
        selected
          ? 'bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))]'
          : 'border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--muted-foreground))]'
      }`}
    >
      {label}
    </button>
  );
}

function PillButton({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] transition-all ${
        selected
          ? 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))]'
          : 'border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--muted-foreground))]'
      }`}
    >
      {label}
    </button>
  );
}

/* ─── Constants ─────────────────────────────────────────────────────── */

// Must match PersonaWizard / ValuationStudioWizard INDUSTRIES exactly
const INDUSTRIES = [
  'Manufacturing',
  'Retail',
  'Technology',
  'Healthcare',
  'Professional Services',
  'Construction',
  'Hospitality',
  'Food & Beverage',
  'Transportation',
  'Real Estate',
  'Financial Services',
  'Other Services',
];

const SIZE_BANDS = ['Under $500K', '$500K\u2013$1M', '$1M\u2013$5M', '$5M\u2013$10M', '$10M+'];

const SELLER_ROLE_OPTIONS = [
  { value: 'owner', title: 'Owner' },
  { value: 'broker', title: 'Broker' },
  { value: 'advisor', title: 'Advisor' },
];

const EXIT_TYPES = [
  { value: 'full-exit', title: 'Full exit' },
  { value: 'majority-sale', title: 'Majority sale' },
  { value: 'minority-sale', title: 'Minority sale' },
  { value: 'family-succession', title: 'Family succession' },
  { value: 'raise-capital', title: 'Raise capital' },
];

const BUYER_TYPE_OPTIONS = [
  { value: 'individual', title: 'Individual buyer' },
  { value: 'search-fund', title: 'Search fund' },
  { value: 'operator', title: 'Operator' },
  { value: 'pe', title: 'PE' },
  { value: 'strategic', title: 'Strategic' },
  { value: 'advisor', title: 'Advisor' },
];

const GEO_OPTIONS = [
  'Canada-wide',
  'Ontario',
  'Quebec',
  'BC',
  'Alberta',
  'Atlantic',
  'Prairies',
  'Territories',
];

const DEAL_ROLE_OPTIONS = ['Lead buyer', 'Investor', 'Advisor', 'Broker'];

/* ─── Helpers ──────────────────────────────────────────────────────── */

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatMemberSince(dateString) {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch (_) {
    return null;
  }
}

function isValidLinkedin(url) {
  if (!url || url.trim() === '') return true; // empty is valid (not required)
  return url.toLowerCase().includes('linkedin.com');
}

/* ─── Profile Component ────────────────────────────────────────────── */

const SucceedoProfile = ({ currentUser, onSave, onUploadAvatar, loading }) => {
  // ── Derive user display values ────────────────────────────────────
  const userName = currentUser?.name || '';
  const userEmail = currentUser?.email || '';
  const userInitials = getInitials(userName);
  const persona = currentUser?.succeedo_persona || 'seller';
  const memberSince = formatMemberSince(currentUser?.created_at);

  // ── State ─────────────────────────────────────────────────────────
  const [photoUrl, setPhotoUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [geography, setGeography] = useState('');

  // Business Details (Seller)
  const [ownerRole, setOwnerRole] = useState(null);
  const [industrySector, setIndustrySector] = useState(null);
  const [industryDescription, setIndustryDescription] = useState('');
  const [businessSizeBand, setBusinessSizeBand] = useState(null);
  const [saleObjective, setSaleObjective] = useState(null);

  // Buyer Criteria
  const [buyerType, setBuyerType] = useState(null);
  const [buyerIndustries, setBuyerIndustries] = useState([]);
  const [targetGeography, setTargetGeography] = useState(null);
  const [targetSize, setTargetSize] = useState(null);
  const [dealRole, setDealRole] = useState(null);

  // UI state
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // ── Initialize from currentUser ───────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;

    setPhotoUrl(currentUser.profile_picture || '');
    setLinkedinUrl(currentUser.linkedin_url || '');
    setCompanyName(currentUser.succeedo_company_name || '');
    setGeography(currentUser.province || '');
    setOwnerRole(currentUser.owner_role || null);
    setIndustrySector(currentUser.industry_sector || null);
    setIndustryDescription(currentUser.industry_description || '');
    setBusinessSizeBand(currentUser.business_size_band || null);
    setSaleObjective(currentUser.sale_objective || null);

    const bp = currentUser.buyer_profile;
    if (bp) {
      setBuyerType(bp.buyer_type || null);
      setBuyerIndustries(bp.preferred_industries || []);
      setTargetGeography(bp.target_geography || null);
      setTargetSize(bp.target_size_range || null);
      setDealRole(bp.deal_role || null);
    }
  }, [currentUser]);

  // ── Snapshot of initial values for change detection ────────────────
  const initialValues = useMemo(() => {
    if (!currentUser) return null;
    const bp = currentUser.buyer_profile;
    return {
      photoUrl: currentUser.profile_picture || '',
      linkedinUrl: currentUser.linkedin_url || '',
      companyName: currentUser.succeedo_company_name || '',
      geography: currentUser.province || '',
      ownerRole: currentUser.owner_role || null,
      industrySector: currentUser.industry_sector || null,
      industryDescription: currentUser.industry_description || '',
      businessSizeBand: currentUser.business_size_band || null,
      saleObjective: currentUser.sale_objective || null,
      buyerType: bp?.buyer_type || null,
      buyerIndustries: bp?.preferred_industries || [],
      targetGeography: bp?.target_geography || null,
      targetSize: bp?.target_size_range || null,
      dealRole: bp?.deal_role || null,
    };
  }, [currentUser]);

  const hasChanges = useMemo(() => {
    if (!initialValues) return false;
    return (
      photoUrl !== initialValues.photoUrl ||
      linkedinUrl !== initialValues.linkedinUrl ||
      companyName !== initialValues.companyName ||
      geography !== initialValues.geography ||
      ownerRole !== initialValues.ownerRole ||
      industrySector !== initialValues.industrySector ||
      industryDescription !== initialValues.industryDescription ||
      businessSizeBand !== initialValues.businessSizeBand ||
      saleObjective !== initialValues.saleObjective ||
      buyerType !== initialValues.buyerType ||
      JSON.stringify(buyerIndustries) !== JSON.stringify(initialValues.buyerIndustries) ||
      targetGeography !== initialValues.targetGeography ||
      targetSize !== initialValues.targetSize ||
      dealRole !== initialValues.dealRole
    );
  }, [
    initialValues,
    photoUrl,
    linkedinUrl,
    companyName,
    geography,
    ownerRole,
    industrySector,
    industryDescription,
    businessSizeBand,
    saleObjective,
    buyerType,
    buyerIndustries,
    targetGeography,
    targetSize,
    dealRole,
  ]);

  // ── Handlers ──────────────────────────────────────────────────────

  const toggleBuyerIndustry = (industry) => {
    if (buyerIndustries.includes(industry)) {
      setBuyerIndustries(buyerIndustries.filter((i) => i !== industry));
    } else {
      setBuyerIndustries([...buyerIndustries, industry]);
    }
  };

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be re-selected if needed
    e.target.value = '';

    // Client-side validation: type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Client-side validation: size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 5MB.`);
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      const result = await onUploadAvatar(file);
      if (result?.avatar_url) {
        setPhotoUrl(result.avatar_url);
      }
      if (window.ahoy) {
        window.ahoy.track('succeedo_profile_avatar_uploaded');
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
      setUploadError(err.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDiscardChanges = () => {
    if (!initialValues) return;
    setPhotoUrl(initialValues.photoUrl);
    setLinkedinUrl(initialValues.linkedinUrl);
    setCompanyName(initialValues.companyName);
    setGeography(initialValues.geography);
    setOwnerRole(initialValues.ownerRole);
    setIndustrySector(initialValues.industrySector);
    setIndustryDescription(initialValues.industryDescription);
    setBusinessSizeBand(initialValues.businessSizeBand);
    setSaleObjective(initialValues.saleObjective);
    setBuyerType(initialValues.buyerType);
    setBuyerIndustries(initialValues.buyerIndustries);
    setTargetGeography(initialValues.targetGeography);
    setTargetSize(initialValues.targetSize);
    setDealRole(initialValues.dealRole);
    setSaveError(null);
  };

  const handleSaveChanges = async () => {
    setSaveError(null);
    setSaved(false);

    const variables = {
      persona: persona,
      goal: currentUser?.succeedo_goal || '',
      timeHorizon: currentUser?.time_horizon || null,
      roleTitle: currentUser?.role_title || null,
      companyName: companyName || null,
      geography: geography || null,
      ownerRole: ownerRole || null,
      industrySector: industrySector || null,
      industryDescription: industryDescription || null,
      businessSizeBand: businessSizeBand || null,
      saleObjective: saleObjective || null,
      buyerType: buyerType || null,
      preferredIndustries: buyerIndustries.length > 0 ? buyerIndustries : null,
      targetGeography: targetGeography || null,
      targetSizeRange: targetSize || null,
      dealRole: dealRole || null,
      linkedinUrl: linkedinUrl || null,
    };

    try {
      await onSave(variables);
      setSaved(true);
      if (window.ahoy) {
        window.ahoy.track('succeedo_profile_updated');
      }
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveError(err.message || 'Failed to save. Please try again.');
    }
  };

  const linkedinError = linkedinUrl && !isValidLinkedin(linkedinUrl);
  const showSeller = persona === 'seller' || persona === 'both';
  const showBuyer = persona === 'buyer' || persona === 'both';

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--page-bg))' }}>
      {/* Page Header */}
      <div className="border-b border-[hsl(var(--border))]" style={{ backgroundColor: 'hsl(var(--background))' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
            <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
              PROFILE
            </div>
          </div>
          <h1 className="font-serif text-[42px] font-light text-[hsl(var(--foreground))] tracking-[-0.02em] leading-[1.1] mb-2">
            Your Profile
          </h1>
          <p className="text-[16px] text-[hsl(var(--muted-foreground))]">
            Manage your account details and business information.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
          {/* LEFT COLUMN - Profile Card */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={userName}
                    className="w-24 h-24 rounded-full object-cover mb-4"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
                    <span className="text-[28px] font-semibold text-[hsl(var(--foreground))]">
                      {userInitials}
                    </span>
                  </div>
                )}

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Upload Photo button triggers file input */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-4 py-2 text-[13px] hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </button>

                {uploadError && (
                  <p className="text-[12px] text-[hsl(var(--destructive))] mt-1 text-center max-w-[200px]">
                    {uploadError}
                  </p>
                )}
              </div>

              {/* Name & Email */}
              <div className="text-center mb-4">
                <div className="text-[18px] font-semibold text-[hsl(var(--foreground))] mb-1">
                  {userName}
                </div>
                <div className="text-[14px] text-[hsl(var(--muted-foreground))]">
                  {userEmail}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-[hsl(var(--border))] my-4" />

              {/* LinkedIn URL */}
              <div className="mb-4">
                <label className="flex items-center gap-2 text-[13px] text-[hsl(var(--muted-foreground))] mb-2">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/yourname"
                  className={`w-full bg-[hsl(var(--background))] border rounded-[8px] px-4 py-2.5 text-[14px] text-[hsl(var(--foreground))] focus:outline-none ${
                    linkedinError
                      ? 'border-[hsl(var(--destructive))] focus:border-[hsl(var(--destructive))]'
                      : 'border-[hsl(var(--input))] focus:border-[hsl(var(--succeedo-orange))]'
                  }`}
                />
                {linkedinError && (
                  <p className="text-[12px] text-[hsl(var(--destructive))] mt-1">
                    Please enter a valid LinkedIn URL
                  </p>
                )}
              </div>

              {/* Persona Badge */}
              <div className="flex justify-center mb-3">
                <div className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] rounded-full px-3 py-1">
                  {persona === 'both' ? 'SELLER \u00B7 BUYER' : persona.toUpperCase()}
                </div>
              </div>

              {/* Member Since */}
              {memberSince && (
                <div className="text-center text-[12px] text-[hsl(var(--muted-foreground))]">
                  Member since {memberSince}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - Editable Sections */}
          <div className="space-y-6">
            {/* SECTION 1 - About You */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-[18px] h-[18px] text-[hsl(var(--muted-foreground))]" strokeWidth={2} />
                <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                  ABOUT YOU
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] text-[hsl(var(--muted-foreground))] mb-2">
                    Company / firm name
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-2.5 text-[hsl(var(--foreground))] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[13px] text-[hsl(var(--muted-foreground))] mb-2">
                    Geography
                  </label>
                  <input
                    type="text"
                    value={geography}
                    onChange={(e) => setGeography(e.target.value)}
                    placeholder="Postal code or province"
                    className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-2.5 text-[hsl(var(--foreground))] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2 - Business Details (Seller) */}
            {showSeller && (
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-[18px] h-[18px] text-[hsl(var(--muted-foreground))]" strokeWidth={2} />
                  <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    BUSINESS DETAILS
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Owner role */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      What&apos;s your role?
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {SELLER_ROLE_OPTIONS.map((option) => (
                        <SelectionCard
                          key={option.value}
                          title={option.title}
                          selected={ownerRole === option.value}
                          onClick={() => setOwnerRole(option.value)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Industry (single-select) */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      What industry?
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {INDUSTRIES.map((industry) => (
                        <IndustryPill
                          key={industry}
                          label={industry}
                          selected={industrySector === industry}
                          onClick={() => setIndustrySector(industry)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Business description */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      Describe your business
                    </label>
                    <textarea
                      value={industryDescription}
                      onChange={(e) => setIndustryDescription(e.target.value)}
                      rows={2}
                      placeholder="e.g., HVAC contracting company serving residential clients in the GTA"
                      className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-2.5 text-[hsl(var(--foreground))] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none resize-none"
                    />
                  </div>

                  {/* Business size */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      Annual revenue range
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {SIZE_BANDS.map((size) => (
                        <PillButton
                          key={size}
                          label={size}
                          selected={businessSizeBand === size}
                          onClick={() => setBusinessSizeBand(size)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Sale objective */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      What are you looking to do?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {EXIT_TYPES.map((option) => (
                        <SelectionCard
                          key={option.value}
                          title={option.title}
                          selected={saleObjective === option.value}
                          onClick={() => setSaleObjective(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 3 - Buyer Criteria */}
            {showBuyer && (
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Search className="w-[18px] h-[18px] text-[hsl(var(--muted-foreground))]" strokeWidth={2} />
                  <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    BUYER CRITERIA
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Buyer type */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      I am a...
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {BUYER_TYPE_OPTIONS.map((option) => (
                        <SelectionCard
                          key={option.value}
                          title={option.title}
                          selected={buyerType === option.value}
                          onClick={() => setBuyerType(option.value)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Preferred industries (multi-select) */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      Industries I&apos;m interested in
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {INDUSTRIES.map((industry) => (
                        <IndustryPill
                          key={industry}
                          label={industry}
                          selected={buyerIndustries.includes(industry)}
                          onClick={() => toggleBuyerIndustry(industry)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Target geography */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      Target geography
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {GEO_OPTIONS.map((geo) => (
                        <PillButton
                          key={geo}
                          label={geo}
                          selected={targetGeography === geo}
                          onClick={() => setTargetGeography(geo)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Target business size */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      Target business size
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {SIZE_BANDS.map((size) => (
                        <PillButton
                          key={size}
                          label={size}
                          selected={targetSize === size}
                          onClick={() => setTargetSize(size)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Deal role */}
                  <div>
                    <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
                      My role in this deal
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {DEAL_ROLE_OPTIONS.map((role) => (
                        <PillButton
                          key={role}
                          label={role}
                          selected={dealRole === role}
                          onClick={() => setDealRole(role)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Sticky Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-[hsl(var(--card))] border-t border-[hsl(var(--border))] shadow-lg z-50">
          <div className="max-w-[1400px] mx-auto px-6 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-[14px] text-[hsl(var(--muted-foreground))]">
                You have unsaved changes
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <button
                  type="button"
                  onClick={handleDiscardChanges}
                  className="text-[14px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                >
                  Discard Changes
                </button>
                <button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={loading || linkedinError}
                  className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-full px-8 py-3 text-[16px] font-medium hover:opacity-90 transition-opacity w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 mt-3">
                <p className="text-red-600 text-[13px]">{saveError}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Toast */}
      {saved && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-green-600 text-white rounded-full px-6 py-3 shadow-lg z-50">
          <div className="text-[14px] font-medium">
            Profile updated successfully
          </div>
        </div>
      )}
    </div>
  );
};

export default SucceedoProfile;
